<template>
  <v-container>
    <v-card class="mx-auto mt-5" max-width="800">
      <v-toolbar color="primary" flat>
        <v-btn icon @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('Sitzungsauswahl') }}</v-toolbar-title>
      </v-toolbar>
      <v-card-subtitle class="mt-2">
        {{ $t('Bitte wählen Sie eine Sitzung aus.') }}
      </v-card-subtitle>

      <v-list v-if="sessions && sessions.length > 0">
        <v-list-item
          v-for="session in sessions"
          :key="session._id"
          @click="selectSession(session)"
          link
        >
          <v-list-item-header>
            <v-list-item-title>
              {{ session.title || session.caseTitle || session.object || $t('Unbekannte Prüfung') }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ formatDate(session._created) }} - {{ session.type || $t('Sitzungen') }}
            </v-list-item-subtitle>
          </v-list-item-header>
          <template v-slot:append>
            <v-chip :color="getStatusColor(session.status)" size="small" class="mr-2">
              {{ session.status || 'Beendet' }}
            </v-chip>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-list-item>
      </v-list>
      <v-card-text v-else-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-card-text>
      <v-card-text v-else class="text-center">
        {{ $t('Keine Sitzungen gefunden.') }}
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import APIServiceRestDB from '@/services/MesskursBackendService';

export default {
  name: 'UserSessionSelection',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const sessions = ref([]);
    const loading = ref(false);
    const courseId = route.params.courseId;

    const fetchSessions = async () => {
      loading.value = true;
      try {
        const userId = store.state.user?.id || store.state.user?._id;
        if (!userId) {
          console.error("No user ID found");
          return;
        }

        const allSessions = await APIServiceRestDB.getSessionsForUser(userId);
        
        // Filter by courseId
        let filteredSessions = allSessions.filter(s => s.course === courseId);

        // Enrich sessions with case titles
        for(let session of filteredSessions) {
            if(session.object) {
                const obj = await APIServiceRestDB.getObjectWithId(session.object);
                if(obj) {
                    session.caseTitle = obj.title;
                    session.type = obj.type;
                }
            }
        }
        sessions.value = filteredSessions;
      } catch (error) {
        console.error("Error fetching sessions for user:", error);
      } finally {
        loading.value = false;
      }
    };

    const selectSession = async (session) => {
      // Set the session in the store
      await store.dispatch('updateSession', session);
      await store.dispatch('updateSessionId', session._id);
      
      const caseId = session.object || session.caseId || session.objectSpecId || session.toolbox || null;
      if (caseId) {
        await store.dispatch('updateCaseId', caseId);
      }

      // Navigate to the appropriate mode
      // Lernen > Ueben > Test
      if (session.study) {
        router.push({ name: 'Lernen', query: { case: caseId, session: session._id } });
      } else if (session.practice) {
        router.push({ name: 'Ueben', query: { case: caseId, session: session._id } });
      } else if (session.test) {
        router.push({ name: 'Test', query: { case: caseId, session: session._id } });
      } else {
        // Fallback to Lernen if nothing specified
        router.push({ name: 'Lernen', query: { case: caseId, session: session._id } });
      }
    };

    const goBack = () => {
      router.push({ name: 'UserCourseSelection' });
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString();
    };

    const getStatusColor = (status) => {
      if (status === 'active') return 'green';
      if (status === 'finished') return 'blue';
      return 'grey';
    };

    onMounted(() => {
      fetchSessions();
    });

    return {
      sessions,
      loading,
      selectSession,
      goBack,
      formatDate,
      getStatusColor
    };
  }
};
</script>
